import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Alert,
  Badge,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
} from "reactstrap";

import { canManageClinicalRecords } from "../../helpers/clinicalRecordAccess";
import {
  ClinicalMapRegion,
  ClinicalMapType,
  ClinicalProtocolMarking,
  createProtocolMarking,
  deleteProtocolMarking,
  displayToQuantityX100,
  listClinicalMapRegions,
  listProtocolMarkings,
  quantityToDisplay,
  updateProtocolMarking,
} from "../../services/clinicalMapService";
import { ClinicalProtocol } from "../../services/clinicalProtocolService";
import { getProducts } from "../../services/productService";
import { Product } from "../../types/product";
import ClinicalMapSchematic from "./ClinicalMapSchematic";

type Props = {
  protocol: ClinicalProtocol;
  canEdit: boolean;
};

type MarkingFormState = {
  regionId: number | null;
  sessionId: string;
  productId: string;
  productLabel: string;
  quantity: string;
  notes: string;
};

const emptyForm = (): MarkingFormState => ({
  regionId: null,
  sessionId: "",
  productId: "",
  productLabel: "",
  quantity: "1",
  notes: "",
});

const ClinicalProtocolMapPanel: React.FC<Props> = ({ protocol, canEdit }) => {
  const { t, i18n } = useTranslation();
  const canManageRegions = canManageClinicalRecords();

  const [mapType, setMapType] = useState<ClinicalMapType>("face");
  const [regions, setRegions] = useState<ClinicalMapRegion[]>([]);
  const [markings, setMarkings] = useState<ClinicalProtocolMarking[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<MarkingFormState>(emptyForm());
  const [editing, setEditing] = useState<ClinicalProtocolMarking | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ClinicalProtocolMarking | null>(null);

  const formatDate = useCallback(
    (value?: string | null) => {
      if (!value) {
        return t("clients.common.notAvailable");
      }
      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value));
    },
    [i18n.language, t]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [regionsRes, markingsRes, productsRes] = await Promise.all([
        listClinicalMapRegions({ map_type: mapType, is_active: true }),
        listProtocolMarkings(protocol.id),
        getProducts({ per_page: 100, product_type: "internal" }).catch(() => ({
          data: [] as Product[],
        })),
      ]);
      setRegions(Array.isArray(regionsRes.data) ? regionsRes.data : []);
      setMarkings(Array.isArray(markingsRes.data) ? markingsRes.data : []);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
    } catch (error: any) {
      toast.error(error?.data || t("clients.protocol.map.loadError"));
      setRegions([]);
      setMarkings([]);
    } finally {
      setLoading(false);
    }
  }, [mapType, protocol.id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredMarkings = useMemo(
    () => markings.filter((m) => m.region?.map_type === mapType),
    [markings, mapType]
  );

  const openCreate = (region: ClinicalMapRegion) => {
    if (!canEdit) {
      return;
    }
    setEditing(null);
    setForm({
      ...emptyForm(),
      regionId: region.id,
      sessionId: protocol.sessions?.find((s) => s.status === "planned")?.id
        ? String(protocol.sessions.find((s) => s.status === "planned")!.id)
        : "",
    });
    setModalOpen(true);
  };

  const openEdit = (marking: ClinicalProtocolMarking) => {
    if (!canEdit) {
      return;
    }
    setEditing(marking);
    setForm({
      regionId: marking.clinical_map_region_id,
      sessionId: marking.clinical_protocol_session_id
        ? String(marking.clinical_protocol_session_id)
        : "",
      productId: marking.product_id ? String(marking.product_id) : "",
      productLabel: marking.product_label ?? "",
      quantity: quantityToDisplay(marking.quantity_x100),
      notes: marking.notes ?? "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const handleSave = async () => {
    if (!form.regionId) {
      toast.error(t("clients.protocol.map.validation.regionRequired"));
      return;
    }
    const quantityX100 = displayToQuantityX100(form.quantity);
    if (quantityX100 === null) {
      toast.error(t("clients.protocol.map.validation.quantityInvalid"));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        clinical_map_region_id: form.regionId,
        clinical_protocol_session_id: form.sessionId
          ? Number(form.sessionId)
          : null,
        product_id: form.productId ? Number(form.productId) : null,
        product_label: form.productLabel.trim() || null,
        quantity_x100: quantityX100,
        notes: form.notes.trim() || null,
      };

      if (editing) {
        await updateProtocolMarking(protocol.id, editing.id, payload);
        toast.success(t("clients.protocol.map.markingUpdated"));
      } else {
        await createProtocolMarking(protocol.id, payload);
        toast.success(t("clients.protocol.map.markingCreated"));
      }
      closeModal();
      await load();
    } catch (error: any) {
      toast.error(error?.data || t("clients.protocol.map.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    setSaving(true);
    try {
      await deleteProtocolMarking(protocol.id, deleteTarget.id);
      toast.success(t("clients.protocol.map.markingDeleted"));
      setDeleteTarget(null);
      await load();
    } catch (error: any) {
      toast.error(error?.data || t("clients.protocol.map.deleteError"));
    } finally {
      setSaving(false);
    }
  };

  const selectedRegion =
    regions.find((r) => r.id === form.regionId) ??
    null;

  return (
    <div className="border rounded p-3 bg-light bg-opacity-50 mt-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <div>
          <h6 className="mb-0">{t("clients.protocol.map.title")}</h6>
          <p className="text-muted small mb-0">{t("clients.protocol.map.hint")}</p>
        </div>
        {canManageRegions ? (
          <Link to="/settings/clinical-map-regions" className="small">
            {t("clients.protocol.map.manageRegions")}
          </Link>
        ) : null}
      </div>

      <Nav tabs className="mb-3">
        <NavItem>
          <NavLink
            href="#"
            className={mapType === "face" ? "active" : ""}
            onClick={(e) => {
              e.preventDefault();
              setMapType("face");
            }}
          >
            {t("clients.protocol.map.face")}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            href="#"
            className={mapType === "body" ? "active" : ""}
            onClick={(e) => {
              e.preventDefault();
              setMapType("body");
            }}
          >
            {t("clients.protocol.map.body")}
          </NavLink>
        </NavItem>
      </Nav>

      {loading ? (
        <div className="text-center py-4">
          <Spinner color="primary" size="sm" />
        </div>
      ) : regions.length === 0 ? (
        <Alert color="info" className="mb-0">
          {t("clients.protocol.map.emptyRegions")}
        </Alert>
      ) : (
        <Row className="g-3">
          <Col md={5}>
            <ClinicalMapSchematic
              mapType={mapType}
              regions={regions}
              markings={filteredMarkings}
              selectedRegionId={form.regionId}
              onSelectRegion={openCreate}
            />
            {canEdit ? (
              <p className="text-muted small text-center mt-2 mb-0">
                {t("clients.protocol.map.clickHint")}
              </p>
            ) : null}
            <div className="d-flex flex-wrap gap-1 justify-content-center mt-2">
              {regions
                .filter((r) => r.has_hotspot)
                .map((region) => (
                  <button
                    key={`chip-${region.id}`}
                    type="button"
                    className="btn btn-sm btn-soft-secondary"
                    disabled={!canEdit}
                    onClick={() => openCreate(region)}
                  >
                    {region.name}
                  </button>
                ))}
            </div>
          </Col>
          <Col md={7}>
            <h6 className="mb-2">{t("clients.protocol.map.historyTitle")}</h6>
            {filteredMarkings.length === 0 ? (
              <Alert color="light" className="mb-0 border">
                {t("clients.protocol.map.emptyMarkings")}
              </Alert>
            ) : (
              <div className="d-flex flex-column gap-2" style={{ maxHeight: 360, overflowY: "auto" }}>
                {filteredMarkings.map((marking) => (
                  <div
                    key={marking.id}
                    className="bg-white border rounded p-2 d-flex justify-content-between gap-2"
                  >
                    <div>
                      <div className="fw-medium">
                        {marking.region?.name ?? `#${marking.clinical_map_region_id}`}
                      </div>
                      <div className="small text-muted">
                        {quantityToDisplay(marking.quantity_x100)}
                        {marking.product?.unit_of_measure
                          ? ` ${marking.product.unit_of_measure}`
                          : ""}
                        {marking.product?.name
                          ? ` · ${marking.product.name}`
                          : marking.product_label
                            ? ` · ${marking.product_label}`
                            : ""}
                        {marking.session
                          ? ` · ${t("clients.protocol.map.sessionLabel", {
                              n: marking.session.session_number,
                            })}`
                          : ""}
                      </div>
                      <div className="small text-muted">
                        {formatDate(marking.recorded_at)}
                        {marking.recorded_by?.name
                          ? ` · ${marking.recorded_by.name}`
                          : ""}
                      </div>
                      {marking.notes ? (
                        <div className="small mt-1">{marking.notes}</div>
                      ) : null}
                    </div>
                    {canEdit ? (
                      <div className="d-flex flex-column gap-1">
                        <Button color="light" size="sm" onClick={() => openEdit(marking)}>
                          {t("clients.protocol.map.edit")}
                        </Button>
                        <Button
                          color="soft-danger"
                          size="sm"
                          onClick={() => setDeleteTarget(marking)}
                        >
                          {t("clients.protocol.map.remove")}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </Col>
        </Row>
      )}

      <Modal isOpen={modalOpen} toggle={closeModal} centered>
        <ModalHeader toggle={closeModal}>
          {editing
            ? t("clients.protocol.map.editTitle")
            : t("clients.protocol.map.addTitle")}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>{t("clients.protocol.map.region")}</Label>
            <Input
              type="select"
              value={form.regionId ?? ""}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  regionId: e.target.value ? Number(e.target.value) : null,
                }))
              }
            >
              <option value="">{t("clients.protocol.map.chooseRegion")}</option>
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </Input>
            {selectedRegion ? (
              <Badge color="soft-primary" className="text-primary mt-1">
                {selectedRegion.name}
              </Badge>
            ) : null}
          </FormGroup>
          <FormGroup>
            <Label>{t("clients.protocol.map.sessionOptional")}</Label>
            <Input
              type="select"
              value={form.sessionId}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, sessionId: e.target.value }))
              }
            >
              <option value="">{t("clients.protocol.map.noSession")}</option>
              {(protocol.sessions ?? []).map((session) => (
                <option key={session.id} value={session.id}>
                  {t("clients.protocol.sessionOf", {
                    n: session.session_number,
                    m: protocol.total_sessions,
                  })}{" "}
                  ({t(`clients.protocol.sessionStatus.${session.status}`)})
                </option>
              ))}
            </Input>
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>{t("clients.protocol.map.productOptional")}</Label>
                <Input
                  type="select"
                  value={form.productId}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, productId: e.target.value }))
                  }
                >
                  <option value="">{t("clients.protocol.map.noProduct")}</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>{t("clients.protocol.map.productLabel")}</Label>
                <Input
                  value={form.productLabel}
                  maxLength={160}
                  placeholder={t("clients.protocol.map.productLabelPlaceholder")}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      productLabel: e.target.value,
                    }))
                  }
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>{t("clients.protocol.map.quantity")}</Label>
            <Input
              value={form.quantity}
              placeholder="1"
              onChange={(e) =>
                setForm((prev) => ({ ...prev, quantity: e.target.value }))
              }
            />
            <p className="text-muted small mb-0 mt-1">
              {t("clients.protocol.map.quantityHint")}
            </p>
          </FormGroup>
          <FormGroup className="mb-0">
            <Label>{t("clients.protocol.map.notes")}</Label>
            <Input
              type="textarea"
              rows={2}
              maxLength={500}
              value={form.notes}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value }))
              }
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeModal} disabled={saving}>
            {t("clients.protocol.map.cancel")}
          </Button>
          <Button color="primary" onClick={() => void handleSave()} disabled={saving}>
            {saving ? t("clients.protocol.saving") : t("clients.protocol.map.save")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={deleteTarget !== null}
        toggle={() => setDeleteTarget(null)}
        centered
      >
        <ModalHeader toggle={() => setDeleteTarget(null)}>
          {t("clients.protocol.map.deleteTitle")}
        </ModalHeader>
        <ModalBody>{t("clients.protocol.map.deleteConfirm")}</ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDeleteTarget(null)}>
            {t("clients.protocol.map.cancel")}
          </Button>
          <Button color="danger" disabled={saving} onClick={() => void handleDelete()}>
            {t("clients.protocol.map.remove")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ClinicalProtocolMapPanel;
